import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import MemberCard from './MemberCard';
import SearchPlayerModal from './SearchPlayerModal';
import TeamTabEmptyState from './TeamTabEmptyState';
import {
  assignCoAdmin,
  fetchTeamPlayers,
  fetchTeamSnapshotPlayers,
  removeCoAdmin,
  removePlayerFromTeamSnapshot,
} from '../api/myTeamApi';
import type { IMember } from '../types/myTeam';
import type { IPlayerSearchResult } from '../types/myTeam';

interface MembersTabProps {
  teamId: string;
  teamName: string;
  captainPlayerId?: string | null;
  viceCaptainPlayerId?: string | null;
  wkPlayerId?: string | null;
  canRemovePlayerFromTeam?: boolean;
  searchModalOpen: boolean;
  onSearchModalOpenChange: (open: boolean) => void;
  teamSnapshotId?: string | null;
  tournamentContextName?: string | null;
  onClearTournamentContext?: () => void;
}

function MembersTab({
  teamId,
  captainPlayerId,
  viceCaptainPlayerId,
  wkPlayerId,
  canRemovePlayerFromTeam,
  searchModalOpen,
  onSearchModalOpenChange,
  teamSnapshotId,
  tournamentContextName,
  onClearTournamentContext,
}: MembersTabProps) {
  const [members, setMembers] = useState<IMember[]>([]);
  const [loading, setLoading] = useState(true);
  const inTournamentContext = Boolean(teamSnapshotId);

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const captainIds = { captainPlayerId, viceCaptainPlayerId, wkPlayerId };
      const list = inTournamentContext
        ? await fetchTeamSnapshotPlayers(teamSnapshotId!, captainIds)
        : await fetchTeamPlayers(teamId, captainIds);
      setMembers(list);
    } catch (err) {
      console.error('Failed to load members', err);
      toast.error(inTournamentContext ? 'Failed to load tournament players.' : 'Failed to load team members.');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [teamId, teamSnapshotId, captainPlayerId, viceCaptainPlayerId, wkPlayerId, inTournamentContext]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleAddPlayers = useCallback(
    async (players: IPlayerSearchResult[]) => {
      if (players.length === 0) return;
      try {
        await assignCoAdmin(teamId, players.map((p) => p.playerId));
        toast.success('Players added successfully.');
        onSearchModalOpenChange(false);
        await loadMembers();
      } catch (err) {
        console.error('Failed to add players', err);
        toast.error('Failed to add players. Please try again.');
      }
    },
    [teamId, loadMembers, onSearchModalOpenChange]
  );

  const handleRemovePlayer = useCallback(
    async (playerId: string) => {
      if (inTournamentContext) {
        if (!teamSnapshotId) return;
        try {
          await removePlayerFromTeamSnapshot(teamSnapshotId, playerId);
          toast.success('Player removed from tournament.');
          await loadMembers();
        } catch (err: unknown) {
          console.error('Failed to remove player from tournament', err);
          const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
          toast.error(msg || 'Failed to remove player. The server may have returned an error.');
        }
        return;
      }
      if (!canRemovePlayerFromTeam) {
        toast.error('You do not have permission to remove players from this team.');
        return;
      }
      try {
        await removeCoAdmin(teamId, [playerId]);
        toast.success('Player removed successfully.');
        await loadMembers();
      } catch (err) {
        console.error('Failed to remove player', err);
        toast.error('Failed to remove player. Please try again.');
      }
    },
    [teamId, teamSnapshotId, canRemovePlayerFromTeam, loadMembers, inTournamentContext]
  );

  if (loading) {
    return (
      <div className="myteam-loading">
        <span>Loading members...</span>
      </div>
    );
  }

  if (members.length === 0) {
    const emptyForTournament = inTournamentContext;
    return (
      <>
        <TeamTabEmptyState
          topActionLabel={emptyForTournament ? undefined : 'Add Player'}
          topActionClassName="myteam-add-player-top"
          onTopAction={emptyForTournament ? undefined : () => onSearchModalOpenChange(true)}
          title={emptyForTournament ? 'No players in this tournament' : 'No players in your squad yet'}
          subtitle={emptyForTournament ? 'There are no players registered for this tournament.' : 'Add players using their Player ID or invite them to join your team.'}
          primaryButtonLabel={emptyForTournament && onClearTournamentContext ? 'View all team members' : 'Add Player'}
          onPrimaryAction={emptyForTournament && onClearTournamentContext ? onClearTournamentContext : () => onSearchModalOpenChange(true)}
        />
        {!emptyForTournament && (
          <SearchPlayerModal
            open={searchModalOpen}
            onOpenChange={onSearchModalOpenChange}
            onAddPlayers={handleAddPlayers}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="myteam-tab-with-content">
        {inTournamentContext && tournamentContextName && onClearTournamentContext && (
          <div className="myteam-tournament-context-header">
            <h3 className="myteam-tournament-context-title">Players in {tournamentContextName}</h3>
            <button
              type="button"
              className="myteam-view-all-members"
              onClick={onClearTournamentContext}
            >
              View all team members
            </button>
          </div>
        )}
        {!inTournamentContext && (
          <button
            type="button"
            className="myteam-add-player-top"
            onClick={() => onSearchModalOpenChange(true)}
          >
            + Add Player
          </button>
        )}
        <div className="myteam-members-grid">
          {members.map((m) => (
            <MemberCard
              key={m.playerId}
              member={m}
              onRemove={handleRemovePlayer}
            />
          ))}
        </div>
      </div>
      {!inTournamentContext && (
        <SearchPlayerModal
          open={searchModalOpen}
          onOpenChange={onSearchModalOpenChange}
          onAddPlayers={handleAddPlayers}
        />
      )}
    </>
  );
}

export default MembersTab;
